import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import dayjs from 'dayjs';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmptyState from '../../../common/EmptyState';
import { TALKING_POINT_STATUSES, getStatusTextColor } from '../../../../constants/talkingPointStatuses';
import { ACTION_ITEM_STATUSES } from './ActionItemCard';
import { VIEW_MODES } from './DiscussionTab';
import { ACTION_ITEM_SUMMARY_COLORS } from '../../../../constants/actionItemColors';
import { toLocalDay } from '../../../../utils/dateOnly';

// Helper functions
const getQuestion = (point) => {
  return point.talkingPoint?.question || point.question || point.systemTalkingPoint?.question;
};

const isSystem = (point) => {
  return point.talkingPoint?.isSystemTalkingPoint;
};

// Group talking points by status
const groupByStatus = (points) => {
  const groups = {
    [TALKING_POINT_STATUSES.PENDING]: [],
    [TALKING_POINT_STATUSES.DISCUSSED]: [],
    [TALKING_POINT_STATUSES.SKIPPED]: [],
  };

  points.forEach(point => {
    const status = point.status || TALKING_POINT_STATUSES.PENDING;
    groups[status].push(point);
  });

  return groups;
};

const getActionItemsSummary = (items) => {
  if (!items || items.length === 0) return null;

  const total = items.length;
  const today = dayjs().startOf('day');

  // Count by status
  const pending = items.filter(i =>
    i.status === ACTION_ITEM_STATUSES.OPEN ||
    i.status === ACTION_ITEM_STATUSES.IN_PROGRESS
  ).length;
  const overdue = items.filter(i =>
    i.deadlineAt &&
    toLocalDay(i.deadlineAt)?.isBefore(today) &&
    i.status !== ACTION_ITEM_STATUSES.DONE &&
    i.status !== ACTION_ITEM_STATUSES.CANCELLED
  ).length;

  const actionText = `${total} action${total > 1 ? 's' : ''}`;

  if (overdue > 0) {
    return { text: actionText, status: `${overdue} overdue`, color: ACTION_ITEM_SUMMARY_COLORS.OVERDUE };
  }
  if (pending === 0 && total > 0) {
    return { text: actionText, status: 'all done', color: ACTION_ITEM_SUMMARY_COLORS.ALL_DONE };
  }
  if (pending > 0) {
    return { text: actionText, status: `${pending} pending`, color: ACTION_ITEM_SUMMARY_COLORS.PENDING };
  }

  return { text: actionText, status: null, color: ACTION_ITEM_SUMMARY_COLORS.DEFAULT };
};

/**
 * DiscussionLeftPanel - Left sidebar for Discussion tab showing talking points and general notes
 *
 * @param {Object} props
 * @param {boolean} props.isLoading - Loading state
 * @param {Array} props.talkingPoints - Array of talking points
 * @param {Object} props.selectedPoint - Currently selected talking point
 * @param {string} props.viewMode - Current view mode ('talking-point' | 'general-notes' | null)
 * @param {Array} props.generalNotes - Array of general notes (for count display)
 * @param {Array} props.actionItems - Array of action items (for summary display)
 * @param {Function} props.onSelectPoint - Callback when a talking point is selected
 * @param {Function} props.onSelectGeneralNotes - Callback when general notes is selected
 */
const DiscussionLeftPanel = ({
  isLoading,
  talkingPoints,
  selectedPoint,
  viewMode,
  generalNotes,
  actionItems,
  onSelectPoint,
  onSelectGeneralNotes,
}) => {
  return (
    <Paper
      sx={{
        width: '22%',
        minWidth: 220,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* AGENDA Section */}
          <Box sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid #E2E8F0',
            borderLeft: '2px solid #3B82F6',
            backgroundColor: '#FAFBFC',
          }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: '#374151',
                letterSpacing: '0.05em',
                fontSize: '11px',
              }}
            >
              AGENDA
            </Typography>
          </Box>

          {talkingPoints.length === 0 ? (
            <Box sx={{ m: 2 }}>
              <EmptyState
                title="No talking points"
                description="Add talking points in Configure tab"
                textSize="h8"
              />
            </Box>
          ) : (
            (() => {
              const grouped = groupByStatus(talkingPoints);
              const statusOrder = [
                TALKING_POINT_STATUSES.PENDING,
                TALKING_POINT_STATUSES.DISCUSSED,
                TALKING_POINT_STATUSES.SKIPPED,
              ];

              // Filter to only groups with points, to track first rendered group
              const nonEmptyGroups = statusOrder.filter(s => grouped[s].length > 0);

              return nonEmptyGroups.map((status, groupIndex) => {
                const points = grouped[status];

                return (
                  <Box key={status}>
                    {/* Status Header */}
                    <Box sx={{
                      px: 2,
                      py: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      borderBottom: '1px solid #F1F5F9',
                    }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getStatusTextColor(status),
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: '#9CA3AF',
                          letterSpacing: '0.05em',
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        {status} · {points.length}
                      </Typography>
                    </Box>

                    {/* Points in this group */}
                    <List sx={{ p: 0 }}>
                      {points.map((point) => (
                        <ListItemButton
                          key={point.id}
                          selected={selectedPoint?.id === point.id && viewMode === VIEW_MODES.TALKING_POINT}
                          onClick={() => onSelectPoint(point)}
                          sx={{
                            borderBottom: '1px solid #F1F5F9',
                            alignItems: 'flex-start',
                            py: 1.5,
                            pl: 2,
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(37, 99, 235, 0.05)',
                              pl: 2.5,
                            },
                            '&.Mui-selected .MuiTypography-root[data-title="true"]': {
                              fontWeight: 600,
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                data-title="true"
                                sx={{
                                  color: '#1F2937',
                                  fontWeight: 500,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {getQuestion(point)}
                              </Typography>
                            }
                            slotProps={{ secondary: { component: 'div' } }}
                            secondary={
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#9CA3AF', fontSize: '11px' }}
                                  >
                                    {isSystem(point) ? 'System' : 'Custom'}
                                  </Typography>
                                  {point.addedByEmployee && (
                                    <>
                                      <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '11px' }}>
                                        •
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '11px' }}>
                                        {point.addedByEmployee.fullName}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                                {(point.notes?.length > 0 || actionItems.filter(item => item.sourceMeetingTalkingPointId === point.id).length > 0) && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                    {point.notes?.length > 0 && (
                                      <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '10px' }}>
                                        {point.notes.length} note{point.notes.length > 1 ? 's' : ''}
                                      </Typography>
                                    )}
                                    {(() => {
                                      const items = actionItems.filter(item => item.sourceMeetingTalkingPointId === point.id);
                                      const summary = getActionItemsSummary(items);
                                      if (!summary) return null;
                                      return (
                                        <Typography variant="caption" sx={{ color: summary.color, fontSize: '10px' }}>
                                          {point.notes?.length > 0 ? '· ' : ''}{summary.text}{summary.status ? ` · ${summary.status}` : ''}
                                        </Typography>
                                      );
                                    })()}
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                );
              });
            })()
          )}

          {/* GENERAL NOTES Section */}
          <Box sx={{
            px: 2,
            py: 1.5,
            mt: 2,
            borderBottom: '1px solid #E2E8F0',
            borderLeft: '2px solid #9CA3AF',
            backgroundColor: '#FAFBFC',
          }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: '#6B7280',
                letterSpacing: '0.05em',
                fontSize: '11px',
              }}
            >
              GENERAL NOTES
            </Typography>
          </Box>

          <List sx={{ p: 0 }}>
            <ListItemButton
              selected={viewMode === VIEW_MODES.GENERAL_NOTES}
              onClick={onSelectGeneralNotes}
              sx={{
                py: 1.5,
                pl: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(37, 99, 235, 0.05)',
                  pl: 2.5,
                },
                '&.Mui-selected .MuiTypography-root[data-title="true"]': {
                  fontWeight: 600,
                },
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                      <DescriptionOutlinedIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                    </Box>
                    <Typography variant="body2" data-title="true" sx={{ fontWeight: 500, color: '#1F2937', flex: 1 }}>
                      Meeting Notes
                    </Typography>
                  </Box>
                }
                slotProps={{ secondary: { component: 'div' } }}
                secondary={
                  <Box sx={{ pl: 3.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#9CA3AF', fontSize: '11px' }}
                      >
                        Meeting-level notes
                      </Typography>
                    </Box>
                    {(generalNotes.length > 0 || actionItems.filter(item => !item.sourceMeetingTalkingPointId).length > 0) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        {generalNotes.length > 0 && (
                          <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '10px' }}>
                            {generalNotes.length} note{generalNotes.length > 1 ? 's' : ''}
                          </Typography>
                        )}
                        {(() => {
                          const items = actionItems.filter(item => !item.sourceMeetingTalkingPointId);
                          const summary = getActionItemsSummary(items);
                          if (!summary) return null;
                          return (
                            <Typography variant="caption" sx={{ color: summary.color, fontSize: '10px' }}>
                              {generalNotes.length > 0 ? '· ' : ''}{summary.text}{summary.status ? ` · ${summary.status}` : ''}
                            </Typography>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          </List>
        </Box>
      )}
    </Paper>
  );
};

export default DiscussionLeftPanel;
